import { useToast } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CustomerComplianceDocument,
  customersApi,
} from '@texas/api/endpoints/customersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { complianceDocumentDefinitionsApi } from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { customerComplianceDocumentEvents } from './events';

export function useAssignCustomerComplianceDocument(
  customerId: number,
  documents: CustomerComplianceDocument[],
  onClose: () => void,
) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    data: definitions,
    refetch: getDefinitions,
    loading: definitionsLoading,
    error: definitionsError,
  } = useApiResource(complianceDocumentDefinitionsApi.getAll);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
  const { request, loading, error } = useApiRequest(
    customersApi.assignComplianceDocument,
  );

  useEffect(() => {
    getDefinitions({
      searchTerm: '',
      includeArchived: false,
      sortBy: 'name',
      sortDesc: false,
    });
  }, [getDefinitions]);

  const { assignedIds, visibleDefinitions, canAssign } = useMemo(() => {
    const assignedIds = new Set(
      documents.map((document) => document.complianceDocumentDefinitionId),
    );
    const query = searchTerm.trim().toLocaleLowerCase();
    const visibleDefinitions = (definitions ?? []).filter(
      (definition) =>
        !definition.archived &&
        `${definition.name} ${definition.description}`
          .toLocaleLowerCase()
          .includes(query),
    );
    const canAssign =
      !loading &&
      !definitionsLoading &&
      !definitionsError &&
      selectedId !== null &&
      !assignedIds.has(selectedId) &&
      visibleDefinitions.some((definition) => definition.id === selectedId);

    return { assignedIds, visibleDefinitions, canAssign };
  }, [
    documents,
    definitions,
    searchTerm,
    loading,
    definitionsLoading,
    definitionsError,
    selectedId,
  ]);

  const assign = async () => {
    if (!canAssign || selectedId === null) return;

    const response = await request(customerId, selectedId, {
      branchIds: selectedBranchIds,
    });
    if (response.hasError) return;

    toast({
      title: t('customerComplianceDocuments.assigned'),
      status: 'success',
      isClosable: true,
    });
    customerComplianceDocumentEvents.changed.dispatch(customerId);
    onClose();
  };

  return {
    assign,
    assignedIds,
    canAssign,
    definitions: visibleDefinitions,
    definitionsError,
    definitionsLoading,
    error,
    loading,
    searchTerm,
    selectedBranchIds,
    selectedId,
    setSelectedBranchIds,
    setSearchTerm,
    setSelectedId,
  };
}
